import { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ClassOutlinedIcon from '@mui/icons-material/ClassOutlined';
import HistoryEduOutlinedIcon from '@mui/icons-material/HistoryEduOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';

import Footer from '../../components/common/Footer';
import Navbar from '../../components/common/Navbar';
import AppSnackbar from '../../components/common/popups/AppSnackbar';
import ScheduleClassModal from '../../components/parent/ScheduleClassModal';
import useParentAuth from '../../hooks/useParentAuth';
import useParentStudents from '../../hooks/useParentStudents';
import useStudentClasses from '../../hooks/useStudentClasses';
import { getFullName } from '../../utils/auth';

const iconPalette = [
  { icon: ClassOutlinedIcon, bg: 'bg-[#3d6df2]', accent: '#3d6df2' },
  { icon: ScienceOutlinedIcon, bg: 'bg-[#69d52b]', accent: '#69d52b' },
  { icon: MenuBookOutlinedIcon, bg: 'bg-[#8d5dff]', accent: '#8d5dff' },
  { icon: HistoryEduOutlinedIcon, bg: 'bg-[#ef4747]', accent: '#ef4747' },
  { icon: SchoolOutlinedIcon, bg: 'bg-[#8e8e8e]', accent: '#8e8e8e' },
];

const getStatusStyles = (status = '') => {
  const value = status.toLowerCase();

  if (value.includes('active')) return 'border-[#46d764] bg-[#46d764]/10 text-[#46d764]';
  if (value.includes('sched')) return 'border-[#f2c300] bg-[#f2c300]/10 text-[#f2c300]';
  if (value.includes('upcoming')) return 'border-[#4b86ff] bg-[#4b86ff]/10 text-[#4b86ff]';
  if (value.includes('cancel')) return 'border-[#ef4747] bg-[#ef4747]/10 text-[#ef4747]';
  if (value.includes('complete')) return 'border-[#b7b7b7] bg-[#b7b7b7]/10 text-[#b7b7b7]';

  return 'border-white/20 bg-white/5 text-white/70';
};

const formatStartDate = (value) => {
  if (!value || value === '--') return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};



function ClassCard({ item, index }) {
  const palette = iconPalette[index % iconPalette.length];
  const Icon = palette.icon;
  const hasTeacherAvatar = Boolean(item.teacherAvatar && item.teacherName && item.teacherName !== '--');
  const hasTeacher = Boolean(item.teacherId && item.teacherName && item.teacherName !== '--');

  return (
    <article className="rounded-[12px] border border-white/[0.08] bg-[#171717] px-4 py-4 shadow-[0_10px_26px_rgba(0,0,0,0.26)] sm:px-5 sm:py-5">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-4">
          <div
            className={`flex h-[72px] w-[72px] items-center justify-center rounded-[12px] text-white ${palette.bg}`}
          >
            <Icon sx={{ fontSize: 34 }} />
          </div>

          <div>
            <h3 className="text-[22px] font-semibold text-white">{item.title}</h3>
            <div
              className="mt-2 inline-flex rounded-[6px] border px-3 py-1 text-sm font-medium"
              style={{ borderColor: palette.accent, color: palette.accent }}
            >
              Class ID: {item.classId}
            </div>
          </div>
        </div>

        <div
          className={`inline-flex items-center gap-2 self-start rounded-[6px] border px-3 py-1 text-sm font-medium ${getStatusStyles(item.status)}`}
        >
          <span className="h-2.5 w-2.5 rounded-full bg-current" />
          <span>{item.status}</span>
        </div>
      </div>

      <div className="mt-5 grid gap-4 border-t border-white/[0.08] pt-4 sm:grid-cols-2 lg:grid-cols-4 lg:gap-0 lg:divide-x lg:divide-white/[0.08] lg:border-t-0 lg:pt-0">
        <div className="lg:px-4 lg:pl-0">
          <div className="flex items-center gap-2 text-white/65">
            <SchoolOutlinedIcon sx={{ fontSize: 18 }} />
            <span className="text-sm">Grade</span>
          </div>
          <div className="mt-2 text-sm text-white">{item.grade}</div>
        </div>
        <div className="lg:px-4">
          <div className="flex items-center gap-2 text-white/65">
            <CalendarMonthOutlinedIcon sx={{ fontSize: 18 }} />
            <span className="text-sm">Class Type</span>
          </div>
          <div className="mt-2 text-sm text-white">{formatStartDate(item.classType)}</div>
        </div>
        <div className="lg:px-4">
          <div className="flex items-center gap-2 text-white/65">
            <SchoolOutlinedIcon sx={{ fontSize: 18 }} />
            <span className="text-sm">Medium</span>
          </div>
          <div className="mt-2 text-sm text-white">{item.medium}</div>
        </div>
        <div className="lg:px-4">
          <div className="flex items-center gap-2 text-white/65">
            <SchoolOutlinedIcon sx={{ fontSize: 18 }} />
            <span className="text-sm">Teacher</span>
          </div>
          <div className="mt-2 flex items-center gap-2 text-sm text-white">
            {hasTeacherAvatar ? (
              <img
                src={`/storage/${item.teacherAvatar}`}
                alt={item.teacherName}
                className="h-7 w-7 shrink-0 rounded-full border border-white/10 object-cover"
              />
            ) : null}
            <span>{item.teacherName}</span>
            {hasTeacher ? (
              <Link
                to={`/teacher/${item.teacherSlug}`}
                target="_blank"
                rel="noreferrer"
                className="ml-1 inline-flex h-7 items-center rounded-full border border-[#f6911d] px-3 text-xs font-semibold text-[#f6911d] transition-colors hover:bg-[#f6911d]/10"
              >
                Review
              </Link>
            ) : (
              <Link
                to="/find-teacher"
                className="ml-1 inline-flex h-7 items-center rounded-full border border-[#f6911d] px-3 text-xs font-semibold text-[#f6911d] transition-colors hover:bg-[#f6911d]/10"
              >
                Find a teacher
              </Link>
            )}
          </div>
        </div>
      </div>

      {/*<div className="mt-4 flex items-center justify-between border-t border-white/[0.08] pt-4">*/}
      {/*  <div>*/}
      {/*    <div className="text-sm text-white/55">Status</div>*/}
      {/*    <div className="mt-2 flex items-center gap-2 text-sm font-medium" style={{ color: item.statusColor || '#46d764' }}>*/}
      {/*      <span className={`h-2.5 w-2.5 rounded-full ${item.statusDot || 'bg-[#46d764]'}`} />*/}
      {/*      <span>{item.status}</span>*/}
      {/*    </div>*/}
      {/*  </div>*/}

      {/*  <button*/}
      {/*    type="button"*/}
      {/*    className="inline-flex h-11 items-center justify-center gap-3 rounded-[2px] border border-[#F6911D] px-5 text-base font-semibold text-[#F6911D] transition-colors hover:bg-[#F6911D]/8"*/}
      {/*  >*/}
      {/*    <span>View Details</span>*/}
      {/*    <ArrowForwardIcon sx={{ fontSize: 18 }} />*/}
      {/*  </button>*/}
      {/*</div>*/}
    </article>
  );
}

const ParentStudentClasses = () => {
  const navigate = useNavigate();
  const { studentId } = useParams();
  const location = useLocation();
  const { user, isParent, isStudent } = useParentAuth();
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { students, loading: parentStudentsLoading } = useParentStudents(isParent);

  const selectedStudent = location.state?.student || null;

  const resolvedStudentId = useMemo(() => {
    if (isStudent) return user?.id ?? user?.student_id ?? null;
    if (selectedStudent?.id) return selectedStudent.id;
    if (studentId) return studentId;
    if (isParent) return students[0]?.id ?? null;
    return null;
  }, [isParent, isStudent, selectedStudent, studentId, students, user]);

  const { student, classes, loading, error, refresh } = useStudentClasses(resolvedStudentId);

  const studentName = useMemo(() => {
    if (student?.first_name || student?.last_name) {
      return getFullName(student);
    }
    if (selectedStudent?.displayName) return selectedStudent.displayName;
    if (isStudent) return getFullName(user);
    return selectedStudent?.name || 'Student';
  }, [isStudent, selectedStudent, student, user]);

  const showBack = isParent;

  const handleScheduleSuccess = async () => {
    await refresh();
    setSnackbar({ open: true, message: 'Class scheduled successfully.', severity: 'success' });
  };

  return (
    <div className="min-h-screen bg-[#0b0b0b] text-white">
      <Navbar />

      <main className="px-4 py-5 sm:px-6 lg:px-8 lg:py-6">
        <section className="mx-auto max-w-[1214px]">
          <article className="overflow-hidden rounded-[16px] border border-white/[0.08] bg-[linear-gradient(135deg,#141414_0%,#101010_55%,#161008_100%)] px-6 py-6 shadow-[0_18px_60px_rgba(0,0,0,0.26)] sm:px-8 sm:py-8 lg:px-5 lg:py-5">
            {showBack ? (
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-2 text-base font-medium text-[#f6911d] hover:text-[#ffb24f]"
              >
                <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
                <span>Back</span>
              </button>
            ) : null}
            <div className="grid gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
              <div className="max-w-[520px]">
                <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#f6911d]">
                  Student Portal
                </p>
                <h1 className="mt-4 text-[34px] font-semibold leading-[1.05] text-white sm:text-[44px] lg:text-[36px]">
                  <span className="text-[#ffffff]"> Hi, {studentName}</span>
                </h1>
                <p className="mt-4 max-w-[560px] text-[15px] leading-7 text-white/65 sm:text-[16px]">
                  {isParent
                    ? 'Select a linked student and review their class schedule.'
                    : 'Review your enrolled classes and upcoming schedule.'}
                </p>

                <button
                  type="button"
                  onClick={() => setScheduleModalOpen(true)}
                  disabled={!resolvedStudentId}
                  className="mt-8 inline-flex h-[50px] items-center justify-center gap-3 rounded-md bg-[#ef8708] px-6 text-[15px] font-semibold text-white shadow-[0_12px_28px_rgba(239,135,8,0.2)] transition-opacity hover:opacity-95"
                >
                  <AddIcon sx={{ fontSize: 22 }} />
                  <span>Schedule a Class</span>
                </button>
              </div>

              <div className="relative min-h-[260px] lg:min-h-[330px]">
                <img className="max-w-[108%]" src="/assets/images/student.png" />
              </div>
            </div>
          </article>

          <article>
            <div className="mt-6 pt-5">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="flex items-center gap-3 text-[#f6911d]">
                    <h2 className="text-[24px] font-semibold text-white">My Classes</h2>
                  </div>
                </div>

                <div className="inline-flex items-center gap-2 rounded-md border border-[#F6911D] px-4 py-2 text-sm font-medium text-[#F6911D]">
                  <span className="flex h-4 w-4 items-center justify-center rounded-sm border border-[#F6911D] text-[10px]">
                    <span>▦</span>
                  </span>
                  <span>{classes.length} Classes</span>
                </div>
              </div>

              {loading || parentStudentsLoading ? (
                <div className="mt-6 rounded-[14px] border border-white/[0.08] bg-[#171717] px-5 py-10 text-center text-white/60">
                  Loading student classes...
                </div>
              ) : null}

              {error ? (
                <div className="mt-6 rounded-[14px] border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                  {error}
                </div>
              ) : null}

              {!loading && !error && classes.length === 0 ? (
                <div className="mt-6 rounded-[14px] border border-white/[0.08] bg-[#171717] px-5 py-10 text-center text-white/60">
                  No classes available for this student.
                </div>
              ) : null}

              <div className="mt-5 space-y-4">
                {classes.map((item, index) => (
                  <ClassCard key={item.id} item={item} index={index} />
                ))}
              </div>
            </div>
          </article>
          <article className="mt-6 rounded-[16px] border border-white/[0.08] bg-[#171717] px-5 py-5 shadow-[0_10px_26px_rgba(0,0,0,0.22)] sm:px-6 sm:py-6">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-start gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#f6911d] text-[#f6911d]">
                  <ShieldOutlinedIcon sx={{ fontSize: 22 }} />
                </div>

                <div>
                  <h3 className="text-[18px] font-semibold text-white">
                    Your data is safe with us
                  </h3>
                  <p className="mt-1 max-w-[640px] text-[15px] leading-7 text-white/60">
                    We use industry-standard security to protect your children&apos;s information
                    and ensure a safe learning environment.
                  </p>
                </div>
              </div>
            </div>
          </article>
        </section>
      </main>

      <Footer />

      <ScheduleClassModal
        open={scheduleModalOpen}
        onClose={() => setScheduleModalOpen(false)}
        studentId={resolvedStudentId}
        studentName={studentName}
        onSuccess={handleScheduleSuccess}
      />

      <AppSnackbar
        open={snackbar.open}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </div>
  );
};

export default ParentStudentClasses;
